import { inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Meta } from '@angular/platform-browser';
import { ActivationStart, Router } from '@angular/router';
import { filter, map } from 'rxjs';

import {
	calculatorSteps,
	hideHeaderRoutesList,
	StepConfig,
	stepInitializerWhiteList,
} from './../utils/header-steps.util';
import { DynamicCalculationFormService } from './dynamic-calculation-form.service';

const themeColors = {
	'bg-primary-180': '#18184B',
	'bg-primary-120': '#554DD2',
};

export type HeaderSteps = {
	name: string;
	route: string;
};

export type HeaderOptions = {
	title?: string;
	steps?: Array<HeaderSteps>;
};

@Injectable({
	providedIn: 'root',
})
export class HeaderService {
	private readonly router = inject(Router);
	private readonly dynamicCalculationFormService = inject(DynamicCalculationFormService);
	public readonly isDealPage = signal<boolean>(false);

	steps = signal<HeaderSteps[]>([]);
	currentStep = signal<number | null>(0);
	menuOpened = signal<boolean>(false);
	allowSideMenu = signal<boolean>(true);
	headerHidden = signal<boolean>(false);
	title = signal<string>('');

	private currentRoutePath?: string;
	private currentRouteParams: Record<string, string> = {};

	constructor(private meta: Meta) {
		this.router.events
			.pipe(
				filter((event) => event instanceof ActivationStart),
				map((event) => event as ActivationStart)
			)
			.subscribe((event) => this.onActivationStart(event));

		toObservable(this.dynamicCalculationFormService.form)
			.pipe(
				takeUntilDestroyed(),
				filter(() => {
					const route = this.currentRoutePath;
					if (!route) return false;
					return stepInitializerWhiteList.some((regex: RegExp) => regex.test(route));
				})
			)
			.subscribe(() => {
				this.initSteps();
			});
	}

	private onActivationStart(event: ActivationStart) {
		this.currentRoutePath = `/${event.snapshot.routeConfig?.path}`;
		this.currentRouteParams = event.snapshot.params;

		if (this.currentRoutePath.includes(StepConfig.NEW_PRODUCT_SURVEY.route)) {
			this.initSteps();
		}

		if (!stepInitializerWhiteList.some((regex: RegExp) => regex.test(this.currentRoutePath as string))) {
			this.resetSteps();
		}

		this.disableStepsInCalculatorPicker();
		this.toggleHeaderVisibility();
		this.setThemeColor();
	}

	private disableStepsInCalculatorPicker() {
		if (this.currentRoutePath?.includes('kalkulatorok')) {
			this.resetSteps();
		}
	}

	private toggleHeaderVisibility(): void {
		this.headerHidden.set(this.isHeaderShouldBeHidden());
	}

	private isHeaderShouldBeHidden(): boolean {
		return hideHeaderRoutesList.some((route) => this.currentRoutePath?.includes(route));
	}

	setThemeColor() {
		const color: keyof typeof themeColors = this.headerHidden() ? 'bg-primary-120' : 'bg-primary-180';
		this.meta.updateTag({ name: 'theme-color', content: themeColors[color] });
	}

	private initSteps() {
		const hasPropertyDataStep = !!this.dynamicCalculationFormService.form()?.value.propertyData;

		const filteredSteps = calculatorSteps.filter(({ name }) => {
			if (!hasPropertyDataStep && name === StepConfig.PROPERTY_QUESTIONS.name) {
				return false;
			}
			return true;
		});

		this.steps.set(filteredSteps);
		this.computeCurrentStep();
	}

	resetSteps() {
		this.steps.set([]);
	}

	update(options: HeaderOptions) {
		this.title.set(options.title ?? this.title());
	}

	openMenu() {
		this.menuOpened.set(true);
	}

	closeMenu() {
		this.menuOpened.set(false);
	}

	getUrlWithParams(url: string, params: Record<string, string>) {
		return url.replace(/(:\w+)/g, (match) => {
			return params[match.slice(1)];
		});
	}

	back(params: Record<string, string> = {}) {
		const step = this.currentStep();
		if (step === null) {
			console.warn(`CurrentStep is null, can't go back`);
			return;
		}
		if (step > 1) {
			// handle params in routes, like /calculation/:id/family-state
			const combinedParams = { ...this.currentRouteParams, ...params };
			const route = this.getUrlWithParams(this.steps()[step - 2].route, combinedParams);
			this.router.navigateByUrl(route);
		} else {
			history.back();
		}
	}

	next(params: Record<string, string> = {}) {
		let step = this.currentStep();

		if (this.isProductSurveyDone() && !step) {
			step = this.findProductSurveyIndex() + 1;
		}

		if (!step) {
			console.warn('current step is not set, defaulting to first');
			step = 0;
		}

		if (!this.steps()[step]) {
			console.error(`Current step (${this.currentStep()}) not exists in steps:`, this.steps());
			return;
		}

		// handle params in routes, like /calculation/:id/family-state
		const combinedParams = { ...this.currentRouteParams, ...params };
		const route = this.getUrlWithParams(this.steps()[step].route, combinedParams);

		this.router.navigateByUrl(route);
	}

	navigateTo(index: number) {
		if (index < this.currentStep()!) {
			const route = this.getUrlWithParams(this.steps()[index - 1].route, this.currentRouteParams);
			this.router.navigateByUrl(route);
		}
	}

	setTitle(title: string) {
		this.title.set(title);
	}

	private computeCurrentStep() {
		const index = this.steps().findIndex((step) => step.route === this.currentRoutePath);
		this.currentStep.set(index > -1 ? index + 1 : null);
	}

	private findProductSurveyIndex = () =>
		this.steps().findIndex(({ route }) => route.includes(StepConfig.NEW_PRODUCT_SURVEY.route));

	private isProductSurveyDone = () => this.dynamicCalculationFormService.productTypes().length > 0;
}
